import { useParams, useSearchParams } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { FormProvider, useFieldArray, useForm, useFormContext, useWatch } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";

import { useGetInvoiceConfig } from "api/queries/invoice/useGetInvoiceConfig";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import Dialog from "components/organisms/Dialog/Dialog";
import Button from "components/atoms/Button/Button";
import Textarea from "components/atoms/Textarea/Textarea";
import usePermissions from "hooks/usePermissions";
import { getUnitSystemPreferences } from "pages/shipper/RequestFormV2/utils/getUnitSystemPreferences";
import HandlingUnitField from "./components/HandlingUnitField/HandlingUnitField";
import ContainerTypeField from "./components/ContainerTypeField/ContainerTypeField";
import LWHFields from "./components/LWHFields/LWHFields";
import WeightField from "./components/WeightField/WeightField";
import CustomsFields from "./components/CustomsFields/CustomsFields";
import PropertyFields from "./components/PropertyFields/PropertyFields";
import UnitsField from "./components/UnitsField/UnitsField";
import OtherDescription from "./components/OtherDescription/OtherDescription";

interface AddItemDialogProps {
  /**
   * The index of the selected commodity
   * If -1, no commodity is selected
   * If 0 or greater, we have a selected commodity and we will prefill the form with its data
   */
  selectedCommodityIndex: number;
  isOpen: boolean;
  onClose: () => void;
  currentInvoice?: any;
}

function AddItemDialog({ selectedCommodityIndex, isOpen, onClose, currentInvoice }: AddItemDialogProps) {
  const { permissions } = usePermissions();
  const { weightUnit, dimensionUnit } = useMemo(() => getUnitSystemPreferences(permissions), [permissions]);
  const methods = useForm({
    mode: "onChange",
    defaultValues: {
      m_unit: dimensionUnit,
      w_unit: weightUnit,
    },
  });
  const { handleSubmit, getValues, setValue, control, trigger } = methods;
  const { control: invoiceFormControl, getValues: getInvoiceFormValues, trigger: triggerInvoiceForm } = useFormContext();
  const {
    append: appendGood,
    remove: removeGood,
    update: updateGood,
  } = useFieldArray({
    control: invoiceFormControl,
    name: "goods",
  });
  const { fields: customsFields, append: appendCustoms } = useFieldArray({
    control,
    name: "good_customs_information",
    keyName: "_id",
  });
  const intl = useIntl();
  const { id: shipmentId } = useParams();
  const [searchParams] = useSearchParams();
  const currentInvoiceId = searchParams.get("invoice");
  const [selectedHandlingUnit] = useWatch({
    name: ["handling_unit"],
    control,
  });
  const { data: invoiceConfig, isLoading: isConfigLoading } = useGetInvoiceConfig({
    shipmentId: shipmentId!,
    currentInvoiceId: currentInvoiceId!,
    options: { refetchOnMount: false },
  }) as any;

  const [selectedHandlingUnitConfig, setSelectedHandlingUnitConfig] = useState<any>(null);

  // Update selectedHandlingUnitConfig when handling_unit field changes
  useEffect(() => {
    if (invoiceConfig) {
      const handlingUnitConfig = invoiceConfig.handling_unit_options.find(
        (option: any) => option.label === selectedHandlingUnit,
      );
      setSelectedHandlingUnitConfig(handlingUnitConfig || invoiceConfig.handling_unit_options[0]);
    }
  }, [selectedHandlingUnit, invoiceConfig]);

  // Seed unit fields for new items (and re-seed if preferences load after mount)
  useEffect(() => {
    if (selectedCommodityIndex !== -1) return;
    if (!getValues("m_unit")) setValue("m_unit", dimensionUnit);
    if (!getValues("w_unit")) setValue("w_unit", weightUnit);
  }, [dimensionUnit, weightUnit, selectedCommodityIndex, getValues, setValue]);

  // If a commodity is selected, prefill the form with its data
  useEffect(() => {
    if (selectedCommodityIndex !== -1) {
      const selectedCommodity = getInvoiceFormValues("goods")[selectedCommodityIndex];

      // Prefill the form with the selected commodity's data
      methods.reset({
        ...selectedCommodity,
        m_unit: selectedCommodity?.m_unit || dimensionUnit,
        w_unit: selectedCommodity?.w_unit || weightUnit,
      });

      // Append any customs fields that were requested for the selected commodity but don't already exist
      appendPreExistingCustomsFields(selectedCommodity);
    }
  }, [selectedCommodityIndex]);

  /**
   * Appends customs fields that were requested for the selected commodity but don't already exist
   * This is necessary incase the user wants to re-add or fill out customs fields that were requested
   * @param selectedCommodity - The selected commodity
   */
  const appendPreExistingCustomsFields = (selectedCommodity: any) => {
    const requestedCustomsInfo = currentInvoice?.good_items?.requested_good_items?.find(
      (item: any) => item.id === selectedCommodity.id,
    )?.good_customs_information;

    if (!requestedCustomsInfo) return;

    requestedCustomsInfo.forEach((requestedCustomsItem: any) => {
      if (
        !selectedCommodity.good_customs_information.find((customsItem: any) => customsItem?.id === requestedCustomsItem?.id)
      ) {
        appendCustoms({
          id: requestedCustomsItem.id,
          hs_code: "",
          value: "",
          description: "",
          currency_code:
            getInvoiceFormValues("shipment.total_invoiced_amount_currency") ||
            getInvoiceFormValues("shipment.total_quoted_amount_currency"),
        });
      }
    });
  };

  /**
   * Validate that fields are filled out correctly
   * @returns {Promise<boolean>} - true if the form is valid
   */
  const validateForm = async (callback: () => void) => {
    const isValid = await trigger(); // Trigger the form validation manually
    if (isValid) callback(); // If the form is valid, execute the provided callback
  };

  /**
   * Remove empty customs fields from the commodity
   * This is necessary because the user can add customs fields without filling them out
   */
  const removeEmptyCustomsFields = () => {
    const commodity = getValues();
    commodity.good_customs_information = commodity.good_customs_information.filter(
      (customsItem: any) => customsItem.description && customsItem.hs_code && customsItem.value,
    );
    setValue("good_customs_information", commodity.good_customs_information);
  };

  // Persist the unit values selected in the dialog (fallback to profile defaults)
  const getCommodityWithUnits = () => {
    const commodity = getValues();
    return {
      ...commodity,
      m_unit: commodity.m_unit || dimensionUnit,
      w_unit: commodity.w_unit || weightUnit,
    };
  };

  // Update the selected commodity in the goods list
  const onSaveItem = async () => {
    validateForm(() => {
      removeEmptyCustomsFields();
      const updatedCommodity = getCommodityWithUnits();
      updatedCommodity.updated = true;
      updateGood(selectedCommodityIndex, updatedCommodity);
      triggerInvoiceForm(`goods`);
      onClose();
    });
  };

  // Add the new commodity to the goods list
  const onAddItem = async () => {
    validateForm(() => {
      removeEmptyCustomsFields();
      appendGood(getCommodityWithUnits());
      onClose();
    });
  };

  // Remove the selected commodity from the goods list
  const onRemoveItem = () => {
    removeGood(selectedCommodityIndex);
    onClose();
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(selectedCommodityIndex !== -1 ? onSaveItem : onAddItem)}>
        <Dialog
          show={isOpen}
          onHide={onClose}
          title={
            selectedCommodityIndex === -1
              ? intl.formatMessage({ id: "billing.invoiceForm.addItem", defaultMessage: "Add Item" })
              : `${intl.formatMessage({ id: "new-request.packing-list.commodity", defaultMessage: "Commodity" })} ${
                  selectedCommodityIndex + 1
                }`
          }
        >
          {isConfigLoading && <LoadingSkeleton rows={1} />}
          {invoiceConfig && !isConfigLoading && (
            <div>
              <div className="d-flex">
                <HandlingUnitField options={invoiceConfig.handling_unit_options} />
                {selectedHandlingUnitConfig?.label === "container" && (
                  <ContainerTypeField options={selectedHandlingUnitConfig.handling_unit_options} />
                )}
                {selectedHandlingUnitConfig?.label === "Other" && (
                  <OtherDescription isRequired={selectedHandlingUnitConfig?.additional_field === "mandatory"} />
                )}
              </div>
              {selectedHandlingUnitConfig && (
                <>
                  {selectedHandlingUnitConfig?.l_w_h !== "off" && selectedHandlingUnitConfig?.label !== "container" && (
                    <>
                      <hr className="add-item-dialog-hr" />
                      <LWHFields
                        isRequired={selectedHandlingUnitConfig?.l_w_h === "mandatory"}
                        defaultMUnit={dimensionUnit}
                      />
                    </>
                  )}
                  <hr className="add-item-dialog-hr" />
                  <div className="d-flex">
                    {selectedHandlingUnitConfig?.quantity !== "off" && (
                      <UnitsField isRequired={selectedHandlingUnitConfig?.quantity === "mandatory"} />
                    )}
                    {selectedHandlingUnitConfig?.weight !== "off" && (
                      <WeightField
                        isRequired={selectedHandlingUnitConfig?.weight === "mandatory"}
                        defaultWUnit={weightUnit}
                      />
                    )}
                  </div>
                  {customsFields && selectedHandlingUnitConfig?.customs_value && (
                    <>
                      <hr className="add-item-dialog-hr" />
                      <CustomsFields
                        selectedCommodityIndex={selectedCommodityIndex}
                        getInvoiceFormValues={getInvoiceFormValues}
                      />
                    </>
                  )}
                  <hr className="add-item-dialog-hr" />
                  <PropertyFields selectedHandlingUnitConfig={selectedHandlingUnitConfig} />
                  <div>
                    <p className="font-b3 mb-2 mt-4">
                      <FormattedMessage
                        id="quote-request.costDetailAdditionalInfomation"
                        defaultMessage="Additional information"
                      />
                    </p>
                    <Textarea
                      name="additional_information"
                      placeholder={intl.formatMessage({
                        id: "billing.invoiceForm.optionalCommentary",
                        defaultMessage: "Optional commentary",
                      })}
                    />
                  </div>
                  {/* Footer */}
                  <div className="d-flex mt-5">
                    <Button
                      variant="outline-primary"
                      label={intl.formatMessage({ id: "general.cancel", defaultMessage: "Cancel" })}
                      onClick={onClose}
                    />
                    {selectedCommodityIndex !== -1 && (
                      <Button
                        label={intl.formatMessage({ id: "general.remove", defaultMessage: "Remove" })}
                        size="lg"
                        variant="danger"
                        className="ml-auto"
                        onClick={onRemoveItem}
                      />
                    )}
                    <Button
                      className={selectedCommodityIndex !== -1 ? "ml-2" : "ml-auto"}
                      size="lg"
                      variant="primary"
                      type="submit"
                      onClick={selectedCommodityIndex !== -1 ? onSaveItem : onAddItem}
                      label={<FormattedMessage id="general.submit" defaultMessage="Submit" />}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </Dialog>
      </form>
    </FormProvider>
  );
}

AddItemDialog.defaultProps = {
  currentInvoice: {},
};

export default AddItemDialog;
